import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        destructive2: 'bg-destructive2 text-destructive2-foreground shadow-sm hover:bg-destructive2/90',
        warning: 'bg-warning text-warning-foreground shadow-sm hover:bg-warning/90',
        success: 'bg-success text-success-foreground shadow-sm hover:bg-success/90',
        export: 'border border-success text-success shadow-sm hover:bg-success/5',
        eggplant: 'bg-[#ab1ee5] text-warning-foreground shadow-sm hover:bg-[#ab1ee5]/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'underline-offset-4 font-normal text-primary hover:text-primary/80',
        black: 'bg-black text-primary-foreground shadow hover:bg-black/80',
        transparent: 'bg-transparent text-primary-foreground border-[1px] border-primary-foreground shadow',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 min-w-9 w-9',
      },
    },
    compoundVariants: [
      {
        variant: 'warning',
        size: 'icon',
        className: 'border border-warning/30 bg-warning/15 text-warning hover:bg-warning/15 h-8 w-8',
      },
      {
        variant: 'primary',
        size: 'icon',
        className: 'border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 h-8 w-8',
      },
      {
        variant: 'secondary',
        size: 'icon',
        className: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 h-8 w-8',
      },
      {
        variant: 'destructive',
        size: 'icon',
        className: 'border border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15 h-8 w-8',
      },
      {
        variant: 'destructive2',
        size: 'icon',
        className: 'border border-destructive2/40 bg-destructive2/10 text-destructive2 hover:bg-destructive2/15 h-8 w-8',
      },
      {
        variant: 'success',
        size: 'icon',
        className: 'border border-success/30 bg-success/10 text-success hover:bg-success/15 h-8 w-8',
      },
      {
        variant: 'black',
        size: 'icon',
        className: 'border border-black/50 bg-black/15 text-black hover:bg-black/15 h-8 w-8',
      },
      {
        variant: 'eggplant',
        size: 'icon',
        className: 'border border-[#ab1ee5]/30 bg-[#ab1ee5]/15 text-[#ab1ee5] hover:bg-[#ab1ee5]/15 h-8 w-8',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
})
Button.displayName = 'Button'

export { Button, buttonVariants }
